import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, Check, FileText, Layers, ListChecks, Search, X } from 'lucide-react';
import { formBuilderApi } from '../../formBuilder/api';
import { FORM_TAG_LABELS, FormSummary, FormTag, Option } from '../../formBuilder/types';
import { iconButton, inputClass, primaryButton, secondaryButton, Skeleton } from '../../formBuilder/ui';

type StartCalibrationDialogProps = {
  onClose: () => void;
  onStart: (formId: string) => void;
};

const TAG_CHOICES: { value: FormTag; hint: string; icon: typeof Layers }[] = [
  { value: 'single_calibration', hint: 'One sensor at a time', icon: Layers },
  { value: 'bulk_calibration', hint: 'Many sensors in one run', icon: ListChecks },
];

// Above this many forms the list gets a search box; below it, scanning is faster than typing.
const SEARCH_THRESHOLD = 4;

const errorMessage = (e: any, fallback: string) => e?.response?.data?.detail || e?.message || fallback;

const sectionLabel = 'mb-1.5 block text-[11px] font-semibold text-slate-700';

/**
 * "Start New Calibration" flow: calibration type -> sensor type -> category -> live form.
 * Each step unlocks the next, and the form list is fetched from the published
 * (runtime) catalog filtered by the first three choices. A single match is
 * pre-selected so the common case is four clicks.
 */
export default function StartCalibrationDialog({ onClose, onStart }: StartCalibrationDialogProps) {
  const [tag, setTag] = useState<FormTag>();
  const [sensorType, setSensorType] = useState('');
  const [category, setCategory] = useState('');
  const [formId, setFormId] = useState('');
  const [search, setSearch] = useState('');

  const [sensorOptions, setSensorOptions] = useState<Option[]>([]);
  const [sensorLoading, setSensorLoading] = useState(true);
  const [sensorError, setSensorError] = useState<string>();

  const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState<string>();
  const [categoryReload, setCategoryReload] = useState(0);

  const [forms, setForms] = useState<FormSummary[]>([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [formsError, setFormsError] = useState<string>();
  const [formsReload, setFormsReload] = useState(0);

  const loadSensorTypes = useCallback(() => {
    setSensorLoading(true);
    setSensorError(undefined);
    formBuilderApi.sensorTypeOptions()
      .then(setSensorOptions)
      .catch((e) => setSensorError(errorMessage(e, 'Failed to load sensor types')))
      .finally(() => setSensorLoading(false));
  }, []);
  useEffect(loadSensorTypes, [loadSensorTypes]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Categories depend on the sensor type: reload (and clear the choice) whenever it changes.
  useEffect(() => {
    setCategory('');
    setCategoryOptions([]);
    setCategoryError(undefined);
    if (!sensorType) return;

    let cancelled = false;
    setCategoryLoading(true);
    formBuilderApi.categoryOptions(sensorType)
      .then((items) => { if (!cancelled) setCategoryOptions(items); })
      .catch((e) => { if (!cancelled) setCategoryError(errorMessage(e, 'Failed to load categories')); })
      .finally(() => { if (!cancelled) setCategoryLoading(false); });
    return () => { cancelled = true; };
  }, [sensorType, categoryReload]);

  // Load the live forms whenever the first three choices are all made.
  useEffect(() => {
    setFormId('');
    setSearch('');
    setForms([]);
    setFormsError(undefined);
    if (!tag || !sensorType || !category) return;

    let cancelled = false;
    setFormsLoading(true);
    formBuilderApi.listRuntime({ sensor_type: sensorType, category, tag })
      .then((items) => {
        if (cancelled) return;
        setForms(items);
        if (items.length === 1) setFormId(items[0].id);
      })
      .catch((e) => { if (!cancelled) setFormsError(errorMessage(e, 'Failed to load forms')); })
      .finally(() => { if (!cancelled) setFormsLoading(false); });
    return () => { cancelled = true; };
  }, [tag, sensorType, category, formsReload]);

  const visibleForms = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return forms;
    return forms.filter((f) => `${f.name} ${f.description ?? ''}`.toLowerCase().includes(q));
  }, [forms, search]);

  const sensorLabel = sensorOptions.find((o) => o.value === sensorType)?.label ?? sensorType;
  const showFormStep = !!tag && !!sensorType && !!category;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-[2px]">
      <div role="dialog" aria-modal="true" aria-labelledby="start-calibration-title"
        className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 id="start-calibration-title" className="text-sm font-semibold text-slate-900">Start new calibration</h2>
            <p className="mt-1 text-[11px] leading-4 text-slate-500">Choose how you are calibrating, then pick the form to fill in.</p>
          </div>
          <button type="button" className={iconButton} onClick={onClose} aria-label="Close dialog"><X className="h-3.5 w-3.5" /></button>
        </div>

        <div className="space-y-5 overflow-y-auto px-5 py-4">
          <fieldset>
            <legend className={sectionLabel}>1. Calibration type</legend>
            <div className="grid grid-cols-2 gap-2" role="radiogroup">
              {TAG_CHOICES.map(({ value, hint, icon: Icon }) => {
                const selected = tag === value;
                return (
                  <button key={value} type="button" role="radio" aria-checked={selected} onClick={() => setTag(value)}
                    className={`relative flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition ${selected ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-100' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}>
                    <Icon className={`h-4 w-4 ${selected ? 'text-primary-600' : 'text-slate-400'}`} />
                    <span className="text-[12px] font-semibold text-slate-800">{FORM_TAG_LABELS[value]}</span>
                    <span className="text-[10px] text-slate-500">{hint}</span>
                    {selected && <Check className="absolute right-2 top-2 h-3.5 w-3.5 text-primary-600" />}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div>
            <label htmlFor="start-calibration-sensor-type" className={sectionLabel}>2. Sensor type</label>
            <select id="start-calibration-sensor-type" className={`${inputClass(!!sensorError)} !mt-0`} value={sensorType}
              disabled={!tag || sensorLoading || !!sensorError} onChange={(e) => setSensorType(e.target.value)}>
              <option value="">
                {!tag ? 'Choose a calibration type first' : sensorLoading ? 'Loading sensor types…' : 'Select a sensor type'}
              </option>
              {sensorOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {sensorError && (
              <span role="alert" className="mt-1 block text-[11px] text-red-600">
                {sensorError} <button type="button" className="font-medium underline" onClick={loadSensorTypes}>Retry</button>
              </span>
            )}
          </div>

          <div>
            <label htmlFor="start-calibration-category" className={sectionLabel}>3. Category</label>
            <select id="start-calibration-category" className={`${inputClass(!!categoryError)} !mt-0`} value={category}
              disabled={!sensorType || categoryLoading || !!categoryError} onChange={(e) => setCategory(e.target.value)}>
              <option value="">
                {!sensorType ? 'Choose a sensor type first' : categoryLoading ? 'Loading categories…' : 'Select a category'}
              </option>
              {categoryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {categoryError && (
              <span role="alert" className="mt-1 block text-[11px] text-red-600">
                {categoryError} <button type="button" className="font-medium underline" onClick={() => setCategoryReload((n) => n + 1)}>Retry</button>
              </span>
            )}
          </div>

          {showFormStep && (
            <div aria-live="polite">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold text-slate-700">4. Form</span>
                {!formsLoading && !formsError && forms.length > 0 && (
                  <span className="text-[10px] text-slate-400">{forms.length} available</span>
                )}
              </div>

              {formsLoading ? (
                <div className="space-y-2" aria-label="Loading forms">
                  <Skeleton className="h-14" />
                  <Skeleton className="h-14" />
                </div>
              ) : formsError ? (
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-[11px] text-red-700">
                  {formsError} <button type="button" className="font-medium underline" onClick={() => setFormsReload((n) => n + 1)}>Retry</button>
                </div>
              ) : forms.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-center">
                  <FileText className="mx-auto h-5 w-5 text-slate-300" />
                  <p className="mt-1.5 text-[12px] font-medium text-slate-700">No live forms yet</p>
                  <p className="mt-0.5 text-[11px] leading-4 text-slate-500">
                    There is no published {FORM_TAG_LABELS[tag as FormTag].toLowerCase()} form for {sensorLabel} / {category}. Ask an admin to publish one in Form Builder.
                  </p>
                </div>
              ) : (
                <>
                  {forms.length >= SEARCH_THRESHOLD && (
                    <div className="relative mb-2">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                      <input type="search" aria-label="Search forms" placeholder="Search forms…" value={search}
                        onChange={(e) => setSearch(e.target.value)} className={`${inputClass()} !mt-0 pl-8`} />
                    </div>
                  )}
                  <div className="max-h-56 space-y-2 overflow-y-auto pr-0.5" role="radiogroup" aria-label="Forms">
                    {visibleForms.map((f) => {
                      const selected = formId === f.id;
                      return (
                        <button key={f.id} type="button" role="radio" aria-checked={selected} onClick={() => setFormId(f.id)}
                          onDoubleClick={() => onStart(f.id)}
                          className={`relative flex w-full items-start gap-2.5 rounded-lg border p-3 text-left transition ${selected ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-100' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}>
                          <FileText className={`mt-0.5 h-4 w-4 shrink-0 ${selected ? 'text-primary-600' : 'text-slate-400'}`} />
                          <span className="min-w-0 flex-1 pr-5">
                            <span className="block truncate text-[12px] font-semibold text-slate-800">{f.name}</span>
                            {f.description && <span className="mt-0.5 line-clamp-2 block text-[11px] leading-4 text-slate-500">{f.description}</span>}
                            <span className="mt-1 block text-[10px] text-slate-400">
                              {f.published_version != null && <>v{f.published_version} · </>}Updated {new Date(f.updated_at).toLocaleDateString()}
                            </span>
                          </span>
                          {selected && <Check className="absolute right-2.5 top-2.5 h-3.5 w-3.5 text-primary-600" />}
                        </button>
                      );
                    })}
                    {visibleForms.length === 0 && (
                      <p className="py-3 text-center text-[11px] text-slate-500">No forms match “{search}”.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button type="button" className={secondaryButton} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryButton} disabled={!formId} onClick={() => formId && onStart(formId)}>
            Start calibration <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
